import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ArrowBack, Delete, Block, GetApp, Search, Refresh } from '@mui/icons-material';
import { useToast } from '../contexts/ToastContext';

interface Subscriber {
  id: number;
  email: string;
  name?: string | null;
  source?: string | null;
  language?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  unsubscribed: boolean;
  unsubscribed_at?: string | null;
  created_at: string;
}

interface SubscriberListResponse {
  total: number;
  items: Subscriber[];
}

const PAGE_SIZE = 100;

const AdminSubscribersPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [items, setItems] = useState<Subscriber[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [includeUnsubscribed, setIncludeUnsubscribed] = useState(true);
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<SubscriberListResponse>('/landing/admin/subscribers', {
        params: {
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          include_unsubscribed: includeUnsubscribed,
        },
      });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (err: any) {
      console.error(err);
      showToast(err?.response?.data?.detail || 'Aboneler yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, includeUnsubscribed, showToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((s) =>
      s.email.toLowerCase().includes(q) ||
      (s.name || '').toLowerCase().includes(q) ||
      (s.source || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const handleDelete = async (id: number, email: string) => {
    if (!window.confirm(`${email} silinsin mi? Bu işlem geri alınamaz.`)) return;
    setActionId(id);
    try {
      await api.delete(`/landing/admin/subscribers/${id}`);
      showToast('Silindi', 'success');
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Silinemedi', 'error');
    } finally {
      setActionId(null);
    }
  };

  const handleUnsubscribe = async (id: number, email: string) => {
    if (!window.confirm(`${email} listeden çıkarılsın mı?`)) return;
    setActionId(id);
    try {
      await api.post(`/landing/admin/subscribers/${id}/unsubscribe`);
      showToast('Aboneliği iptal edildi', 'success');
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Güncellenemedi', 'error');
    } finally {
      setActionId(null);
    }
  };

  const handleExportCsv = () => {
    const rows = filtered.map((s) => ({
      id: s.id,
      email: s.email,
      name: s.name || '',
      source: s.source || '',
      language: s.language || '',
      unsubscribed: s.unsubscribed ? 'yes' : 'no',
      unsubscribed_at: s.unsubscribed_at || '',
      created_at: s.created_at,
    }));
    const headers = Object.keys(rows[0] || { id: '', email: '' });
    const csv = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => {
        const v = String((r as any)[h] ?? '');
        return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `landing-subscribers-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDate = (s: string) => {
    try {
      return new Date(s).toLocaleString('tr-TR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return s; }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4 transition-colors"
        >
          <ArrowBack sx={{ fontSize: 16 }} />
          <span>Admin Paneli</span>
        </button>

        {/* Header */}
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
              Mail Aboneleri
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Landing page'deki "İrtibat" formundan toplanan e-postalar.
              Toplam: <span className="font-semibold">{total}</span>
              {!includeUnsubscribed && ' (yalnızca aktif)'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <Refresh className={loading ? 'animate-spin' : ''} sx={{ fontSize: 16 }} />
              Yenile
            </button>
            <button
              onClick={handleExportCsv}
              disabled={filtered.length === 0}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50"
            >
              <GetApp sx={{ fontSize: 16 }} />
              CSV İndir
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" sx={{ fontSize: 16 }} />
            <input
              type="text"
              placeholder="E-posta, isim veya kaynak ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={includeUnsubscribed}
              onChange={(e) => { setIncludeUnsubscribed(e.target.checked); setPage(0); }}
              className="rounded"
            />
            Çıkanları da göster
          </label>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
          {loading && items.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400 text-sm">
              Yükleniyor…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400 text-sm">
              {search ? 'Aramaya uyan kayıt yok.' : 'Henüz abone yok.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">E-posta</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Kaynak</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Dil</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Tarih</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Durum</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {filtered.map((s, idx) => (
                    <tr key={s.id} className={`hover:bg-gray-50 dark:hover:bg-gray-900 ${s.unsubscribed ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-500 tabular-nums">{idx + 1 + page * PAGE_SIZE}</td>
                      <td className="px-4 py-3">
                        <a href={`mailto:${s.email}`} className="text-gray-900 dark:text-gray-100 hover:underline font-medium">{s.email}</a>
                        {s.name && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.name}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-xs">{s.source || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-xs uppercase">{s.language || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap text-xs">{formatDate(s.created_at)}</td>
                      <td className="px-4 py-3">
                        {s.unsubscribed ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Çıktı</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">Aktif</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {!s.unsubscribed && (
                          <button
                            onClick={() => handleUnsubscribe(s.id, s.email)}
                            disabled={actionId === s.id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-md mr-1 disabled:opacity-50"
                            title="Aboneliği iptal et"
                          >
                            <Block sx={{ fontSize: 14 }} />
                            Çıkar
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(s.id, s.email)}
                          disabled={actionId === s.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md disabled:opacity-50"
                          title="Kaydı sil"
                        >
                          <Delete sx={{ fontSize: 14 }} />
                          Sil
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>
              Sayfa {page + 1} / {Math.ceil(total / PAGE_SIZE)}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Önceki
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= total || loading}
                className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubscribersPage;
